import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircularProgress, Stack } from '@mui/material';
import { ConnectionLineType, Controls, Panel, ReactFlow, ReactFlowProvider, useNodesState, useReactFlow } from '@xyflow/react';
import objectHash from 'object-hash';
import { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { EdgeToFlowEdge, NEMToFlow } from '../../../dataVisualization/ReactFlow/codec';
import { useEntityNetwork } from '../hooks/useEntityNetwork';
import { getMostProximateVisibleChildren, getMostProximateVisibleParents } from '../queries';
import { defaultEntityNetworkSchematicContext, EntityNetworkSchematicContext, EntityNetworkSchematicContextReducer } from './context';
import { resolveNode } from './nodes';
import { Buttons, fitViewOptions } from './panels/Buttons';
import { Configuration } from './panels/Configuration';
import { Visibility } from './panels/Visibility';
import { useIsValidConnection } from './utilities/connections';
import { getLayoutedElements } from './utilities/layout';
// required for some reason, doesn't quite work without it
import '@xyflow/react/dist/style.css';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * Renders a (graphical) schematic of an {@link EntityNetwork | `EntityNetwork`} (or sub-class)
 *
 * Can simply be slotted into an `ObjectCard` as one of the `additionalTabs`
 *
 * TODO: [CRA] carefully calculated height for the default object header and tab row
 * I should do it differently, but I can't think of a reasonable fix for it due to existing technical debt
 * Thusly, "tech debt begets tech debt"
 */
export const EntityNetworkSchematicTab = (props) => {
    return (_jsx(Stack, { height: "calc(100cqh - 7.5em)", children: _jsx(EntityNetworkSchematicContextProvider, { ...props }) }));
};
/**
 * Renders a (graphical) schematic of an {@link EntityNetwork | `EntityNetwork`} (or sub-class)
 *
 * **Has no intrinsic height — will grow to fill the flex container!**
 *
 * @example
 * <Stack height={...}>
 *  <EntityNetworkSchematicContextProvider {...props} />
 * </Stack>
 */
export const EntityNetworkSchematicContextProvider = (props) => {
    const [context, dispatch] = useReducer(EntityNetworkSchematicContextReducer, defaultEntityNetworkSchematicContext);
    return (_jsx(EntityNetworkSchematicContext.Provider, { value: [context, dispatch], children: _jsx(ReactFlowProvider, { children: _jsx(EntityNetworkSchematicInternal, { ...props }) }) }));
};
export const EntityNetworkSchematicInternal = ({ applicationData, viewObject, UserFunctions, isSmallView, fetch }) => {
    const [context] = useContext(EntityNetworkSchematicContext);
    const { rankDirection, edgeType, markerType, edgeWidth, markerSize: markerWidth } = context;
    const { fitView } = useReactFlow();
    useEffect(() => {
        fitView(fitViewOptions);
    }, [rankDirection]);
    const { entityNetwork, nem, loading } = useEntityNetwork({
        entityNetwork: viewObject.matchedPrimary,
        applicationData,
        UserFunctions,
        fetch,
    });
    const vobTypes = Object.keys(nem.containedTypes());
    const [hiddenTypes, setHiddenTypes] = useState([]);
    useEffect(() => setHiddenTypes([]), [objectHash(vobTypes)]);
    /**
     * *"It's important that the nodeTypes are memoized or defined ouside of the component."*
     *
     * https://reactflow.dev/learn/customization/custom-nodes#adding-the-node-type
     */
    const nodeTypes = useMemo(() => {
        debug && console.log('EntityNetworkSchematicInternal', 'resolving node types');
        return Object.fromEntries(vobTypes.map((t) => [t, resolveNode(t)]));
    }, [vobTypes]);
    const { isValidConnection } = useIsValidConnection({ nem });
    // transform VOBs and edges to something that ReactFlow understands
    const { nodes: encodedNodes, edges: encodedEdges } = NEMToFlow(nem, {
        type: edgeType,
        markerEnd: markerType ? { type: markerType, width: markerWidth } : undefined,
    });
    const filteredNodes = encodedNodes.filter((n) => !hiddenTypes.includes(n.type ?? ''));
    const filteredEdges = encodedEdges.filter((e) => {
        const parent = nem.getPrimaryItem(e.source);
        const child = nem.getPrimaryItem(e.target);
        const filterParent = parent ? hiddenTypes.includes(parent.type) : false;
        const filterChild = child ? hiddenTypes.includes(child.type) : false;
        const filter = !filterParent || !filterChild;
        debug && console.log('EntityNetworkSchematicInternal', 'filter edge?', parent?.name, child?.name, filterParent, filterChild, filter);
        return filter;
    });
    const hiddenNodes = encodedNodes.filter((n) => hiddenTypes.includes(n.type ?? ''));
    const additionalEdges = hiddenNodes
        .map((node) => {
        const vob = node.data;
        const parents = getMostProximateVisibleParents(vob, nem, hiddenTypes);
        const children = getMostProximateVisibleChildren(vob, nem, hiddenTypes);
        return parents
            .map((p, pi) => children.map((c, ci) => {
            /**
             * **For visual display only, don't write-back to the datamodel!**
             *
             * TODO: [CRA] this `edgeTypeId` is clearly bogus, but we're doing things to the data that isn't in accordance with the datamodel
             */
            const edgeTypeId = `${p.type}_has_${c.type}`;
            const edge = { ...c, edgeTypeId };
            debug && console.log('EntityNetworkSchematicInternal', 'adding additional edge', edgeTypeId);
            return EdgeToFlowEdge(p, edge, c, { type: edgeType, markerEnd: markerType ? { type: markerType, width: markerWidth } : undefined }, `${pi}:${ci}`);
        }))
            .flat();
    })
        .flat()
        // deduplicate edges
        .filter((e, i, arr) => i === arr.findIndex((v) => v.source === e.source && v.target === e.target && v.data?.edgeTypeId === e.data?.edgeTypeId));
    const combinedEdges = [...filteredEdges, ...additionalEdges];
    // perform graph layout using nodes & edges
    const { nodes, edges } = getLayoutedElements(filteredNodes, combinedEdges, context);
    const [statefulNodes, setStatefulNodes, onNodesChange] = useNodesState([...nodes]);
    useEffect(() => setStatefulNodes(nodes), [context, filteredNodes.length, combinedEdges.length]);
    return (_jsxs(Stack, { position: "relative", flexGrow: 1, children: [_jsx(Stack, { flexGrow: 1, children: _jsxs(ReactFlow, { nodes: statefulNodes, onNodesChange: onNodesChange, edges: edges, nodeTypes: nodeTypes, connectionLineType: ConnectionLineType.SimpleBezier, isValidConnection: isValidConnection, maxZoom: 4, minZoom: 0.125, fitView: true, fitViewOptions: fitViewOptions, nodesDraggable: false, edgesFocusable: false, style: {
                        '--xy-edge-stroke-width-default': edgeWidth,
                        '--xy-connectionline-stroke-width-default': edgeWidth,
                    }, children: [_jsx(Controls, { showFitView: false, showInteractive: false }), _jsx(Panel, { position: "top-left", style: { margin: !isSmallView ? undefined : '0.5em' }, children: _jsx(Configuration, { entityNetwork: entityNetwork, isSmallView: isSmallView }) }), _jsx(Panel, { position: "top-center", style: { margin: !isSmallView ? undefined : '0.5em' }, children: _jsx(Buttons, { loading: loading, isSmallView: isSmallView }) }), _jsx(Panel, { position: "top-right", style: { margin: !isSmallView ? undefined : '0.5em' }, children: _jsx(Visibility, { types: vobTypes, hiddenTypes: hiddenTypes, setHiddenTypes: setHiddenTypes, isSmallView: isSmallView }) })] }) }), loading && _jsx(CircularProgress, { size: 96, sx: { position: 'absolute', top: '50%', left: '50%', translate: '-50% -50%' } })] }));
};
